import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import {
  Button,
  ButtonGroup,
  Fieldset,
  Form,
  TextInput,
  fieldKey,
  useControlled,
} from "@datapay-by-datacom/design-system";
import type {
  CompanyConfigProfileInput,
  CompanyConfigVersion,
  CompanySettingsProfile,
} from "../../generated/core-api";
import { WorkflowActionBar } from "../../shared";
import {
  useSaveCompanyConfig,
  useUnsavedProgressNavigationGuard,
} from "../hooks";
import {
  COMPANY_CONFIG_DISPLAY_NAME_MAX_LENGTH,
  CS_BOOLEAN_PARENT,
  displayNameValidationError,
  emptyCompanySettingsProfile,
  emptyMaintainCompanyConfigForm,
  emptyPayGroupSettingsProfile,
  emptyProfile,
  type CompanySettingsBooleanKey,
  type CsErrors,
  type MaintainCompanyConfigFormValue,
  type OptionGroup,
  type PgNumberErrors,
} from "../types";
import { backendErrorMessage } from "../utils/backendErrorMessage";
import { CopySettingsProfile } from "./CopySettingsProfile";
import { PublishCompanyConfigConfirmModal } from "./PublishCompanyConfigConfirmModal";
import { UnsavedProgressConfirmModal } from "./UnsavedProgressConfirmModal";
import s from "./MaintainCompanyConfigForm.module.css";

/**
 * Returns `true` when every value in `obj` is a no-op default (`false`,
 * `null`, or `undefined`). Used to decide whether a sub-profile that was
 * lazy-initialised to its empty factory (all-false booleans + null URL) has
 * actually been touched by the user. If it hasn't, we strip it from the
 * outgoing payload so create requests don't post empty objects and legacy
 * configs that lack the sub-profile aren't implicitly written back.
 */
function isAllDefaults(obj: object): boolean {
  return Object.values(obj).every((v) => v === false || v === null || v === undefined);
}

const ADDITIONAL_RESOURCES_URL_ERROR =
  "Enter a valid http:// or https:// URL (e.g. https://example.com).";

/**
 * Returns `true` when `str` looks like a valid `http:` or `https:` URL.
 * Uses the built-in `URL` parser — invalid input throws, so protocol check
 * after a successful parse is enough. Matches R-AR-5 / AC-OPT-04 / AC-OPT-05
 * and the API's `_DIRECT_ACCESS_URL_PATTERN` (`https?://…`).
 */
function hasForbiddenUrlCharacters(value: string): boolean {
  for (const char of value) {
    const code = char.charCodeAt(0);
    if (code <= 0x1f || code === 0x7f) {
      return true;
    }
    if (char === " " || char === "\t" || char === "\n" || char === "\r") {
      return true;
    }
  }
  return false;
}

/**
 * Parse and normalise an http(s) URL for Additional Resources.
 * Returns `null` when the value fails the same rules as the API's
 * `_DIRECT_ACCESS_URL_PATTERN` (explicit scheme, no whitespace/control chars).
 */
function parseHttpUrl(str: string): string | null {
  const trimmed = str.trim();
  if (!/^https?:\/\//i.test(trimmed)) {
    return null;
  }
  if (hasForbiddenUrlCharacters(trimmed)) {
    return null;
  }
  try {
    const url = new URL(trimmed);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return null;
    }
    return url.href;
  } catch {
    return null;
  }
}

function isLikelyUrl(str: string): boolean {
  return parseHttpUrl(str) !== null;
}

/**
 * Force every dependent `cs-boolean` to `false` when its parent (per
 * {@link CS_BOOLEAN_PARENT}) is off. Returns a new object only when a change is
 * needed; otherwise the input is returned unchanged.
 *
 * The edit form's coupling gate is display-only (it never rewrites the stored
 * value), so a config loaded/copied with an inconsistent
 * `{parent: false, dependent: true}` pair would otherwise persist the stale
 * `true`. Normalising here — at submit time, before the `isAllDefaults` strip —
 * makes the persisted payload match what the user sees, and lets an
 * otherwise-empty CompanySettings still strip correctly.
 */
function normalizeCsCoupling(cs: CompanySettingsProfile): CompanySettingsProfile {
  let next = cs;
  for (const [dependent, parent] of Object.entries(CS_BOOLEAN_PARENT) as [
    CompanySettingsBooleanKey,
    CompanySettingsBooleanKey,
  ][]) {
    const parentOn = cs[parent] ?? false;
    if (!parentOn && cs[dependent]) {
      next = { ...next, [dependent]: false };
    }
  }
  return next;
}

/**
 * Strip optional sub-profiles from the profile when they are absent or
 * contain only defaults so we don't post empty objects on create (or
 * materialise all-false sub-profiles onto legacy configs that never had them).
 *
 * Applies to: `PayGroupOptions`, `CompanySettings`, `PayGroupSettings`.
 * `CompanyOptions` is the required root section and is never stripped.
 *
 * `PayGroupOptions` uses the existing `Object.keys === 0` check (its empty
 * factory is `{}`). `CompanySettings` / `PayGroupSettings` use `isAllDefaults`
 * because their empty factories carry explicit false/null values.
 *
 * `CompanySettings` is first passed through {@link normalizeCsCoupling} so a
 * dependent boolean whose parent is off is forced to `false` before the strip
 * runs.
 */
function cleanProfile(profile: CompanyConfigProfileInput): CompanyConfigProfileInput {
  let result = profile;

  const pg = result.PayGroupOptions;
  if (pg === undefined || Object.keys(pg).length === 0) {
    // PayGroupOptions is optional on CompanyConfigProfileInput — safe to omit.
    const { PayGroupOptions: _pg, ...rest } = result;
    result = rest;
  }

  let cs = result.CompanySettings;
  if (cs !== undefined) {
    // Normalise the coupling gate onto the stored value before stripping, so a
    // stale dependent `true` can't survive a save.
    cs = normalizeCsCoupling(cs);
    const rawUrl = cs.SetDirectAccessAdditionalResourcesUrl;
    if (typeof rawUrl === "string" && rawUrl !== "") {
      const normalizedUrl = parseHttpUrl(rawUrl);
      if (normalizedUrl !== null) {
        cs = { ...cs, SetDirectAccessAdditionalResourcesUrl: normalizedUrl };
      }
    }
    result = { ...result, CompanySettings: cs };
  }
  if (cs === undefined || isAllDefaults(cs)) {
    const { CompanySettings: _cs, ...rest } = result;
    result = rest;
  }

  const pgs = result.PayGroupSettings;
  if (pgs === undefined || isAllDefaults(pgs)) {
    const { PayGroupSettings: _pgs, ...rest } = result;
    result = rest;
  }

  return result;
}

interface MaintainCompanyConfigFormProps {
  /** "create" mints a new company configuration; "edit" saves a new version onto `initial`. */
  mode: "create" | "edit";
  /** The current published version, required in edit mode. */
  initial?: CompanyConfigVersion;
  /** Keep the original form layout or use the catalogue redesign presentation. */
  presentation?: "legacy" | "redesign";
  /** Destination used by Back, Cancel, and successful saves. */
  cataloguePath?: string;
}

function toFormValue(
  initial: CompanyConfigVersion | undefined
): MaintainCompanyConfigFormValue {
  if (!initial) {
    return emptyMaintainCompanyConfigForm();
  }
  return {
    display_name: initial.display_name,
    change_description: "",
    profile: {
      ...initial.profile,
      // CompanyOptions is declared non-optional on CompanyConfigProfileInput/Output,
      // but legacy persisted (Cosmos) records pre-date the field and lack it at
      // runtime — so this guard deliberately defends against data that defies the
      // declared schema. `?? emptyProfile()` gives the form a valid all-false
      // starting state. Note: saving then materialises that all-false CompanyOptions
      // into the version chain for such configs.
      // Follow-up: the proper fix is to correct the OpenAPI spec so CompanyOptions
      // is marked optional, which would also remove the `as ...` casts in the tests.
      CompanyOptions: initial.profile.CompanyOptions ?? emptyProfile(),
      // CompanySettings and PayGroupSettings are optional on the profile — legacy
      // configs may omit them. Lazy-default to all-false so the form fields have a
      // valid starting state without forcing empty objects onto unedited payloads
      // (cleanProfile() strips them back out if they remain untouched on save).
      CompanySettings: initial.profile.CompanySettings ?? emptyCompanySettingsProfile(),
      PayGroupSettings: initial.profile.PayGroupSettings ?? emptyPayGroupSettingsProfile(),
    },
  };
}

export function MaintainCompanyConfigForm({
  mode,
  initial,
  presentation = "redesign",
  cataloguePath = "/onboarding",
}: MaintainCompanyConfigFormProps) {
  const navigate = useNavigate();
  const saveMutation = useSaveCompanyConfig();
  const formRootRef = useRef<HTMLDivElement>(null);
  const [submitted, setSubmitted] = useState(false);
  const [isPublishConfirmOpen, setIsPublishConfirmOpen] = useState(false);
  const isRedesign = presentation === "redesign";
  const [profileTab, setProfileTab] = useState<{
    group: OptionGroup;
    resetKey: number;
  }>({ group: "datapay", resetKey: 0 });
  // Chrome sets document.body.scrollTop when scroll-into-view fires on a focused
  // element (even with overflow:hidden on body). Attached to document (not the
  // container ref) so it also fires for portalled elements that render outside
  // the container DOM.
  useEffect(() => {
    let rafId = -1;
    const onFocusIn = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (document.body.scrollTop !== 0) document.body.scrollTop = 0;
        if (document.documentElement.scrollTop !== 0)
          document.documentElement.scrollTop = 0;
      });
    };
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const initialValue = useMemo(() => toFormValue(initial), [initial]);
  const field = useControlled(initialValue);
  const input = fieldKey(field);
  const value = field.value;
  const isDirty = JSON.stringify(value) !== JSON.stringify(initialValue);
  const navigationGuard = useUnsavedProgressNavigationGuard(isDirty);

  const escalationOn = value.profile.CompanyOptions.EnableLeaveRequestEscalation ?? false;
  const escalationDays = value.profile.CompanyOptions.LeaveRequestEscalation;

  // Pay Group conditional number errors (mirror the escalation-days pattern).
  // Three checkbox→number pairs require > 0 when their parent is on;
  // FinalReportDistributionProcessTime is bounded 0–23 whenever it has a value.
  const pg = value.profile.PayGroupOptions;
  const pgNumberErrors: PgNumberErrors = {};
  if (pg?.AutoEndDateUserAfterTermination) {
    const days = pg.DaysAfterTerminationDate;
    if (!(typeof days === "number" && days > 0)) {
      pgNumberErrors.DaysAfterTerminationDate = "Enter a day count greater than 0.";
    }
  }
  if (pg?.EnablePayRunAuthorisation) {
    const num = pg.MinNumOfPayRunAuthorisers;
    if (!(typeof num === "number" && num > 0)) {
      pgNumberErrors.MinNumOfPayRunAuthorisers = "Enter a number greater than 0.";
    }
  }
  if (pg?.AutomaticallyFinaliseMainPayRun) {
    const days = pg.DaysToFinaliseAfterDCDate;
    if (!(typeof days === "number" && days > 0)) {
      pgNumberErrors.DaysToFinaliseAfterDCDate = "Enter a day count greater than 0.";
    }
  }
  if (pg?.FinalReportDistributionProcessTime !== undefined && pg.FinalReportDistributionProcessTime !== null) {
    const time = pg.FinalReportDistributionProcessTime;
    if (!(typeof time === "number" && time >= 0 && time <= 23)) {
      pgNumberErrors.FinalReportDistributionProcessTime = "Enter an hour between 0 and 23.";
    }
  }

  // Company Settings string-field validation.
  // The URL field is optional — only validate when a value has been entered.
  const cs = value.profile.CompanySettings;
  const csErrors: CsErrors = {};
  const urlValue = cs?.SetDirectAccessAdditionalResourcesUrl;
  const directAccessOn = cs?.EnableDirectAccess ?? false;
  if (urlValue !== null && urlValue !== undefined && urlValue !== "") {
    if (!directAccessOn) {
      // A URL is meaningless without Direct Access — flag the inconsistent combo.
      csErrors.SetDirectAccessAdditionalResourcesUrl =
        "Enable Direct Access to set an Additional Resources URL.";
    } else if (!isLikelyUrl(urlValue)) {
      csErrors.SetDirectAccessAdditionalResourcesUrl = ADDITIONAL_RESOURCES_URL_ERROR;
    }
  }

  const errors = {
    display_name: displayNameValidationError(value.display_name, {
      enforceMaxLength:
        mode === "create" ||
        value.display_name.trim() !== (initial?.display_name ?? "").trim(),
    }),
    change_description: !value.change_description.trim()
      ? "Change reason is required."
      : "",
    escalation:
      escalationOn && !(typeof escalationDays === "number" && escalationDays > 0)
        ? "Enter a day count greater than 0."
        : "",
  };
  const isValid =
    Object.values(errors).every((message) => !message) &&
    Object.values(pgNumberErrors).every((message) => !message) &&
    Object.values(csErrors).every((message) => !message);
  const validationErrorCount = [
    ...Object.values(errors),
    ...Object.values(pgNumberErrors),
    ...Object.values(csErrors),
  ].filter(Boolean).length;

  const saveConfiguration = () => {
    setIsPublishConfirmOpen(false);
    const onSuccess = (result: CompanyConfigVersion) => {
      navigationGuard.bypassNextNavigation();
      if (mode === "create") {
        navigate(`${cataloguePath}/${result.config_code}`);
        return;
      }
      navigate(cataloguePath, {
        state: {
          statusMessage:
            isRedesign
              ? "Company configuration version published successfully."
              : "Company configuration saved successfully.",
        },
      });
    };

    if (mode === "create") {
      saveMutation.mutate(
        {
          mode: "create",
          body: {
            display_name: value.display_name.trim(),
            change_description: value.change_description.trim(),
            profile: cleanProfile(value.profile),
          },
        },
        { onSuccess }
      );
    } else if (initial) {
      saveMutation.mutate(
        {
          mode: "version",
          configCode: initial.config_code,
          body: {
            display_name: value.display_name.trim(),
            change_description: value.change_description.trim(),
            profile: cleanProfile(value.profile),
            based_on: initial.version ?? null,
          },
        },
        { onSuccess }
      );
    }
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (!isValid) {
      const hasDatapayProfileError =
        Boolean(pgNumberErrors.MinNumOfPayRunAuthorisers) ||
        Boolean(pgNumberErrors.DaysToFinaliseAfterDCDate) ||
        Boolean(pgNumberErrors.FinalReportDistributionProcessTime);
      const hasDirectAccessProfileError =
        Boolean(errors.escalation) ||
        Boolean(pgNumberErrors.DaysAfterTerminationDate) ||
        Object.values(csErrors).some(Boolean);
      const identityHasError =
        Boolean(errors.display_name) ||
        Boolean(errors.change_description);

      if (!identityHasError && (hasDatapayProfileError || hasDirectAccessProfileError)) {
        setProfileTab((current) => ({
          group: hasDatapayProfileError ? "datapay" : "direct-access",
          resetKey: current.resetKey + 1,
        }));
      }

      window.setTimeout(() => {
        const firstInvalid = formRootRef.current?.querySelector<HTMLElement>(
          'input[aria-invalid="true"], textarea[aria-invalid="true"], select[aria-invalid="true"], [aria-invalid="true"] input, [aria-invalid="true"] textarea, [aria-invalid="true"] select'
        );
        firstInvalid?.focus();
      }, 0);
      return;
    }

    if (isRedesign) {
      setIsPublishConfirmOpen(true);
      return;
    }

    saveConfiguration();
  };

  const show = (message: string) => (submitted ? message : "");
  const identityFields = (
    <div className={s.row}>
      <div>
        <TextInput
          label="Display name"
          placeholder="Education industry shell"
          {...input("display_name")}
          isRequired
          aria-describedby={
            mode === "create" ? "display-name-max-length-hint" : undefined
          }
          maxLength={
            mode === "create" ? COMPANY_CONFIG_DISPLAY_NAME_MAX_LENGTH : undefined
          }
          errorMessage={show(errors.display_name)}
          isInvalid={Boolean(show(errors.display_name))}
        />
        {mode === "create" && (
          <p className={s.fieldHint} id="display-name-max-length-hint">
            Maximum {COMPANY_CONFIG_DISPLAY_NAME_MAX_LENGTH} characters
          </p>
        )}
      </div>
    </div>
  );
  const changeReasonField = (
    <TextInput
      label="Change reason"
      placeholder="Describe what changed and why"
      {...input("change_description")}
      isRequired
      errorMessage={show(errors.change_description)}
      isInvalid={Boolean(show(errors.change_description))}
    />
  );
  const versionActions =
    mode === "edit" && initial ? (
      <>
        <Button
          variant="tertiary"
          onPress={() =>
            navigate(`${cataloguePath}/${initial.config_code}/versions`)
          }
        >
          Version history
        </Button>
        <Button
          variant="tertiary"
          onPress={() =>
            navigate(
              `${cataloguePath}/${initial.config_code}/versions/${initial.version}/retire`
            )
          }
        >
          Retire…
        </Button>
      </>
    ) : null;
  const saveButton = (
    <Button
      variant="primary"
      type="submit"
      isDisabled={saveMutation.isPending}
    >
      {isRedesign
        ? saveMutation.isPending
          ? mode === "create"
            ? "Creating…"
            : "Publishing…"
          : mode === "create"
            ? "Create configuration"
            : "Publish new version"
        : saveMutation.isPending
          ? "Saving…"
          : "Save version"}
    </Button>
  );
  const profileEditor = (
    <CopySettingsProfile
      profile={value.profile}
      onChange={(profile) => input("profile").onChange(profile)}
      presentation={isRedesign ? "review" : "tabs"}
      escalationError={show(errors.escalation)}
      pgNumberErrors={submitted ? pgNumberErrors : {}}
      csErrors={submitted ? csErrors : {}}
      defaultGroup={profileTab.group}
      tabResetKey={profileTab.resetKey}
    />
  );
  const saveError = saveMutation.isError ? (
    <div className={s.error} role="alert">
      {backendErrorMessage(
        saveMutation.error,
        "Could not save this company configuration. Please try again."
      )}
    </div>
  ) : null;
  const validationSummary =
    submitted && !isValid ? (
      <div className={s.error} role="alert">
        {validationErrorCount} field{validationErrorCount === 1 ? "" : "s"}{" "}
        {validationErrorCount === 1 ? "needs" : "need"} attention. Review the
        highlighted fields.
      </div>
    ) : null;

  return (
    <div
      ref={formRootRef}
      className={`${s.container} ${isRedesign ? s.redesignContainer : ""}`}
    >
      <Link to={cataloguePath} className={s.backLink}>
        &larr; Back to company configurations
      </Link>

      <header className={s.header}>
        <h1 className={s.title}>
          {initial ? "Maintain company configuration" : "New company configuration"}
        </h1>
        <p className={s.subtitle}>
          {initial
            ? `${initial.display_name} · v${initial.version} · ${initial.status}`
            : "A version 1 will be published on save"}
        </p>
        {initial && (
          <p className={s.idLine}>
            Company configuration id: {initial.config_code}
          </p>
        )}
      </header>

      <Form onSubmit={handleSubmit}>
        <Fieldset>
          {isRedesign ? (
            <div className={s.redesignForm}>
              {validationSummary}
              <section
                className={s.detailsCard}
                aria-labelledby="configuration-details-heading"
              >
                <h2
                  id="configuration-details-heading"
                  className={s.sectionHeading}
                >
                  Configuration details
                </h2>
                {identityFields}
                {changeReasonField}
              </section>

              {profileEditor}
              {saveError}

              <WorkflowActionBar
                ariaLabel="Configuration actions"
                leadingActions={
                  versionActions ? (
                    <div className={s.versionActions}>{versionActions}</div>
                  ) : undefined
                }
                trailingActions={
                  <ButtonGroup align="right">
                    <Button
                      variant="secondary"
                      onPress={() => navigate(cataloguePath)}
                    >
                      Cancel
                    </Button>
                    {saveButton}
                  </ButtonGroup>
                }
              />
            </div>
          ) : (
            <>
              {validationSummary}
              {identityFields}
              {profileEditor}
              {changeReasonField}
              {saveError}
              <ButtonGroup align="right">
                {versionActions}
                {saveButton}
              </ButtonGroup>
            </>
          )}
        </Fieldset>
      </Form>
      {isRedesign && (
        <PublishCompanyConfigConfirmModal
          isOpen={isPublishConfirmOpen && !navigationGuard.isBlocked}
          mode={mode}
          configCode={initial?.config_code}
          displayName={value.display_name.trim()}
          changeReason={value.change_description.trim()}
          isPending={saveMutation.isPending}
          onCancel={() => setIsPublishConfirmOpen(false)}
          onConfirm={saveConfiguration}
        />
      )}
      <UnsavedProgressConfirmModal
        isOpen={navigationGuard.isBlocked}
        onStay={navigationGuard.stay}
        onLeave={navigationGuard.leaveAnyway}
        title="Leave company configuration?"
        message="Your unsaved company configuration changes will be lost."
      />
    </div>
  );
}
